/* eslint-disable react-native/no-inline-styles */
/*
Author: Rendra Pramulia
*/

import React, { useState, useEffect } from 'react';
import { StyleSheet, View, TouchableOpacity, Dimensions } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import Toast from 'react-native-toast-message';

import SociollaText from '@components/sociolla/SociollaText';
import ModalAddress from './ModalAddress';
import Address from './Address';

import Colors from '@global/colors';
import { isWeb } from '@global/screens';
import {
  setDeliveryAddress,
  setInvoiceAddress,
  setStoreAddress,
  setNpwpAddress,
  setKtpAddress,
} from '@/store/customer';
import { showAlert } from '@/store/global';
import ModalSelectBox from '@components/modal/ModalSelectBox';
import PropTypes from 'prop-types';
import { isVn } from '@global/helpers';

const propTypes = {
  data: PropTypes.object,
  state: PropTypes.any,
};

const CustomerAddress = (props) => {
  const dispatch = useDispatch();
  const delivery = useSelector((state) => state.customer.delivery_address);
  const invoice = useSelector((state) => state.customer.invoice_address);
  const npwp = useSelector((state) => state.customer.npwp_address);
  const ktp = useSelector((state) => state.customer.ktp_address);
  const store = useSelector((state) => state.customer.store_address);

  const contentLabel = [
    { label: `Business Tax Code ${isVn() ? '' : '(NPWP)'}`, value: 'npwp' },
    { label: `Identification Number ${isVn() ? '' : '(KTP)'}`, value: 'ktp' },
    { label: 'Delivery Address', value: 'delivery' },
    { label: 'Invoice Address', value: 'invoice' },
    { label: 'Store Address', value: 'store' },
  ];

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [isEdit, setIsEdit] = useState(false);
  const [tempIdx, setTempIdx] = useState(1);
  const [showModalAddressType, setShowModalAddressType] = useState(false);
  const [addressType, setAddressType] = useState('');
  const [addressData, setAddressData] = useState(null);

  const setModal = (show, addressType) => {
    setShowModal(show);
    setModalType(addressType);
  };

  const setInvoice = (data) => dispatch(setInvoiceAddress(data));
  const setDelivery = (data) => dispatch(setDeliveryAddress(data));
  const setNpwp = (data) => dispatch(setNpwpAddress(data));
  const setKtp = (data) => dispatch(setKtpAddress(data));
  const setStore = (data) => dispatch(setStoreAddress(data));

  const onDeleteAddress = (data, index, type) => {
    dispatch(
      showAlert({
        show: true,
        title: 'Remove Address',
        description: 'This address will be deleted. Do you want to proceed?',
        onCallback: () => {
          if (type === 'invoice') {
            const newData = [...invoice];

            newData.splice(index, 1);
            setInvoice(newData);
          } else if (type === 'delivery') {
            const newData = [...delivery];

            newData.splice(index, 1);
            setDelivery(newData);
          } else if (type === 'npwp') {
            setNpwp({});
          } else if (type === 'ktp') {
            setKtp({});
          } else if (type === 'store') {
            const newData = [...store];
            newData.splice(index, 1);
            setStore(newData);
          }
        },
      }),
    );
  };

  const onChangeAddressType = (value) => {
    setAddressType(value.value);
    setShowModal(true);
    setShowModalAddressType(false);
  };

  const onSubmit = (data, type) => {
    const payload = { ...data };
    const invoiceData = [...invoice];
    const deliveryData = [...delivery];
    const storeData = [...store];
    const npwpData = payload;
    const ktpData = payload;

    if (type === 'delivery') {
      const normalizedName = (payload.name || '').trim().toLowerCase();
      const isDuplicateName = deliveryData.some((existing) => {
        const existingName = (existing?.name || '').trim().toLowerCase();
        if (!existingName) {
          return false;
        }

        if (payload._id && existing._id === payload._id) {
          return false;
        }

        if (payload.tempIdx && existing.tempIdx === payload.tempIdx) {
          return false;
        }

        return existingName === normalizedName;
      });

      if (isDuplicateName) {
        Toast.show({
          text1: 'Delivery name must be unique',
          type: 'error',
        });
        return;
      }
    }

    if (type === 'invoice') {
      if (!payload._id) {
        if (!payload.tempIdx) {
          invoiceData.push({ ...payload, tempIdx: tempIdx });
          setTempIdx((prevTempIdx) => prevTempIdx + 1);
        } else {
          const index = invoiceData.findIndex(
            (inv) => inv?.tempIdx === payload?.tempIdx,
          );

          if (index !== -1) {
            invoiceData[index] = payload;
          }
        }

        setInvoice(invoiceData);
      } else {
        const index = invoiceData.findIndex((inv) => inv._id === payload._id);

        if (index !== -1) {
          invoiceData[index] = payload;
          setInvoice(invoiceData);
        }
      }
    } else if (type === 'delivery') {
      if (!payload._id) {
        if (!payload.tempIdx) {
          deliveryData.push({ ...payload, tempIdx: tempIdx });
          setTempIdx((prevTempIdx) => prevTempIdx + 1);
        } else {
          const index = deliveryData.findIndex(
            (inv) => inv?.tempIdx === payload?.tempIdx,
          );

          if (index !== -1) {
            deliveryData[index] = payload;
          }
        }

        setDelivery(deliveryData);
      } else {
        const index = deliveryData.findIndex((inv) => inv._id === payload._id);

        if (index !== -1) {
          deliveryData[index] = payload;
          setDelivery(deliveryData);
        }
      }
    } else if (type === 'npwp') {
      setNpwp(npwpData);
    } else if (type === 'ktp') {
      setKtp(ktpData);
    } else if (type === 'store') {
      if (!payload._id) {
        if (!payload.tempIdx) {
          storeData.push({ ...payload, tempIdx: tempIdx });
          setTempIdx((prevTempIdx) => prevTempIdx + 1);
        } else {
          const index = storeData.findIndex(
            (inv) => inv?.tempIdx === payload?.tempIdx,
          );
          if (index !== -1) {
            storeData[index] = payload;
          }
        }
        setStore(storeData);
      } else {
        const index = storeData.findIndex((inv) => inv._id === payload._id);
        if (index !== -1) {
          storeData[index] = payload;
          setStore(storeData);
        }
      }
    }
  };

  const showModalAddress = (show, data = null, addressType = '') => {
    if (data) {
      setIsEdit(true);
      setAddressType(data.type);
      setAddressData({
        ...data.data,
        market_type: props.data.market_type || '',
        business_type: props.data.market_type?.business_type || '',
      });
    } else {
      setIsEdit(false);
      setAddressType('');
      setAddressData(null);
    }
    setModal(show, addressType);
  };

  useEffect(() => {
    let mounted = true;
    if (props.data && mounted) {
      if (props.data?.delivery_address?.length > 0) {
        setDelivery(props.data.delivery_address);
      }
      if (props.data?.invoice_address?.length > 0) {
        setInvoice(props.data.invoice_address);
      }
      if (props.data.ktp_address || props.data.ktp) {
        setKtp({
          ...props.data.ktp_address,
          ktp: props.data.ktp_address?.identification_number ?? props.data.ktp,
        });
      }
      if (props.data.vat_address || props.data.vat) {
        setNpwp({
          ...props.data.vat_address,
          vat: props.data.vat_address?.identification_number ?? props.data.vat,
        });
      }
      if (props.data.store_address?.length > 0) {
        setStore(props.data.store_address);
      }
    }
    return () => (mounted = false);
  }, [props.data]);

  const renderHeaderTab = () => (
    <View style={styles.headerTabContainer}>
      <SociollaText type="medium" style={styles.headerTabText}>
        Customer Address
      </SociollaText>
    </View>
  );

  const validateDataAddress = (addressType) => {
    if (addressType === 'delivery') {
      return delivery.length > 0;
    }
    if (addressType === 'invoice') {
      return invoice.length > 0;
    }
    if (addressType === 'ktp') {
      return Object.keys(ktp).length > 0;
    }
    if (addressType === 'store') {
      return store.length > 0;
    }

    return Object.keys(npwp).length > 0;
  };

  const isNotIdentity = (addressType) => {
    return addressType !== 'ktp' && addressType !== 'npwp';
  };

  const renderAddMore = (hasValue, addressType) => {
    if (hasValue) {
      return (
        <TouchableOpacity
          testID={`customer-address-add-more-${addressType}-btn`}
          onPress={() => showModalAddress(true, null, addressType)}>
          <SociollaText style={styles.addMoreText}>+ Add Address</SociollaText>
        </TouchableOpacity>
      );
    }
  };

  const renderLabel = (label) => (
    <SociollaText type="medium" style={styles.headerGridText}>
      {label}
    </SociollaText>
  );

  const renderHeaderGrid = ({ label, value }) => {
    const hasValue = validateDataAddress(value) && isNotIdentity(value);

    return (
      <View
        style={[
          styles.headerGridContainer,
          { justifyContent: hasValue ? 'space-between' : 'flex-start' },
        ]}>
        {renderLabel(label)}
        {renderAddMore(hasValue, value)}
      </View>
    );
  };

  const getResponsiveCardWidth = () => {
    if (!isWeb) {
      return 330;
    }

    const screenWidth = Dimensions.get('window').width;
    const maxContainerWidth = 1200;
    const effectiveWidth = Math.min(screenWidth, maxContainerWidth) - 90;
    const columns = 2;
    const spacing = 20;

    const totalGap = spacing * (columns - 1);
    const cardWidth = (effectiveWidth - totalGap) / columns;

    return Math.floor(cardWidth);
  };

  const renderAddAddress = (addressType) => (
    <View
      key={addressType}
      style={[styles.gridItem, { width: getResponsiveCardWidth() }]}>
      <TouchableOpacity
        testID={`customer-address-add-${addressType}-btn`}
        style={styles.addAddressButton}
        onPress={() => showModalAddress(true, null, addressType)}>
        <SociollaText style={styles.addAddressText}>+ Add Address</SociollaText>
      </TouchableOpacity>
    </View>
  );

  const getDatAddress = (addressType) => {
    if (addressType === 'delivery') {
      return delivery;
    }
    if (addressType === 'invoice') {
      return invoice;
    }
    if (addressType === 'ktp') {
      return [ktp];
    }
    if (addressType === 'store') {
      return store;
    }

    return [npwp];
  };

  const renderDataAddress = (addressType) => {
    const dataAddress = getDatAddress(addressType);

    return (
      <Address
        addressType={addressType}
        data={dataAddress}
        onDelete={onDeleteAddress}
        onAddAddress={() => setShowModalAddressType(true)}
        onPressAddress={(data) => showModalAddress(true, data, addressType)}
      />
    );
  };

  const renderAddressContent = (item) => {
    const { value: addressType } = item;
    const isNotEmptyAddress = validateDataAddress(addressType);

    return isNotEmptyAddress
      ? renderDataAddress(addressType)
      : renderAddAddress(addressType);
  };

  const renderContainerAddress = (item, idx) => (
    <View style={styles.containerAddress} key={idx}>
      {renderHeaderGrid(item)}
      {renderAddressContent(item)}
    </View>
  );

  const renderContentWeb = () => (
    <View style={styles.gridContainer}>
      {contentLabel.map((item, idx) => renderContainerAddress(item, idx))}
    </View>
  );

  const renderContentSection = () => (
    <React.Fragment>
      {isWeb && renderHeaderTab()}
      {renderContentWeb()}
    </React.Fragment>
  );

  const renderModalAddress = () => (
    <ModalAddress
      testID={`customer-address-modal-${modalType}`}
      addressType={addressType}
      show={showModal}
      onClose={() => showModalAddress(false)}
      onSubmit={onSubmit}
      data={isEdit ? addressData : props.data}
      edit={isEdit}
      state={props.state}
      modalType={modalType}
    />
  );

  const renderModalSelectBox = () => (
    <ModalSelectBox
      testID="customer-address-type-select-modal"
      title={'Address Type'}
      show={showModalAddressType}
      onClose={() => setShowModalAddressType(false)}
      options={contentLabel}
      labelKey="label"
      onChange={onChangeAddressType}
    />
  );

  const renderModalSection = () => (
    <React.Fragment>
      {showModal && renderModalAddress()}
      {showModalAddressType && renderModalSelectBox()}
    </React.Fragment>
  );

  return (
    <View testID="customer-address-container" style={styles.container}>
      {renderContentSection()}
      {renderModalSection()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: 500,
  },
  headerTabContainer: {
    flexDirection: 'row',
    width: '100%',
    marginBottom: 30,
  },
  headerGridContainer: {
    marginTop: 20,
    marginBottom: 20,
    display: 'flex',
    flexDirection: 'row',
  },
  headerTabText: {
    fontSize: 16,
    color: Colors.black2,
  },
  headerGridText: {
    fontSize: 14,
    color: Colors.black3,
    textAlign: 'left',
  },
  gridContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginHorizontal: 10,
  },
  gridItem: {
    backgroundColor: Colors.grey15,
    borderRadius: 6,
    padding: 20,
    marginBottom: 20,
    alignItems: 'center',
    height: 126,
  },
  addAddressButton: {
    borderRadius: 1,
    padding: 10,
    marginTop: 25,
  },
  addAddressText: {
    color: Colors.blue0,
    fontWeight: '600',
    textAlign: 'center',
    letterSpacing: 1,
    fontSize: 15,
  },
  containerAddress: {
    marginBottom: isWeb ? 30 : 10,
  },
  addMoreText: {
    color: Colors.blue0,
    fontWeight: '600',
    letterSpacing: 1,
    fontSize: 15,
  },
});

CustomerAddress.propTypes = propTypes;

export default CustomerAddress;
